// src/pages/patient/Doctors.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { patientDoctorsApi } from "../../services/patientDoctors";

const PAGE_SIZE = 12;

function classNames(...arr) {
  return arr.filter(Boolean).join(" ");
}

function parseApiError(err, fallback = "Something went wrong. Please try again.") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((x) => x?.msg || x?.message || JSON.stringify(x)).join(", ");
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return err?.response?.data?.message || err?.message || fallback;
}

function Icon({ name, className = "h-5 w-5" }) {
  const common = { className, viewBox: "0 0 24 24", fill: "none" };
  if (name === "doctor") return <svg {...common}><path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" stroke="currentColor" strokeWidth="2"/><path d="M4 21a8 8 0 0 1 16 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M12 15v5M9.5 17.5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>;
  if (name === "search") return <svg {...common}><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/><path d="m20 20-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>;
  if (name === "refresh") return <svg {...common}><path d="M20 12a8 8 0 1 1-2.34-5.66" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M20 4v6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
  if (name === "bell") return <svg {...common}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" stroke="currentColor" strokeWidth="2"/><path d="M10 21h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>;
  if (name === "message") return <svg {...common}><path d="M21 12a8 8 0 0 1-8 8H6l-3 2 1.2-4.1A8 8 0 1 1 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>;
  if (name === "calendar") return <svg {...common}><path d="M7 3v3M17 3v3M4 8h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><rect x="4" y="6" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="2"/></svg>;
  return null;
}

function Pill({ children, tone = "neutral" }) {
  const tones = {
    neutral: "bg-slate-50 text-slate-700 border-slate-200",
    info: "bg-sky-50 text-sky-700 border-sky-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return <span className={classNames("inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold", tones[tone] || tones.neutral)}>{children}</span>;
}

function initials(name) {
  return String(name || "DR").trim().split(/\s+/).map((x) => x[0]).join("").slice(0, 2).toUpperCase() || "DR";
}

function formatMessageTime(value) {
  if (!value) return "No messages yet";
  try {
    return new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" }).format(new Date(value));
  } catch {
    return value;
  }
}

function doctorPhoto(item) {
  return item.doctor_photo_url || item.photo_url || item.profile_photo_url || item.avatar_url || item.doctor?.photo_url || null;
}

function doctorName(item) {
  return item.doctor_name || item.doctor_display_name || item.doctor?.display_name || item.doctor?.full_name || "Doctor";
}

function specialtyLabel(item) {
  const specs = Array.isArray(item.specializations) ? item.specializations.filter(Boolean) : [];
  return item.specialty || specs[0] || item.primary_specialization || item.doctor_specialty || item.doctor?.specialty || "Care provider";
}

function clinicLabel(item) {
  return item.clinic_name || item.clinic?.name || "Independent practice";
}

function EmptyState({ requests }) {
  return (
    <div className="rounded-3xl border border-border bg-white p-10 text-center shadow-soft">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-sky-50 text-primary"><Icon name="doctor" className="h-8 w-8" /></div>
      <div className="mt-4 text-lg font-semibold">No doctors yet</div>
      <div className="mt-2 text-sm text-muted">When a doctor requests access and you approve it, they will appear here.</div>
      {requests.length > 0 ? <Link to="/patient/doctor-requests" className="btn-primary-gradient mt-5 inline-flex rounded-2xl px-5 py-3 text-sm font-semibold text-white">Review requests</Link> : null}
    </div>
  );
}

export default function Doctors() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const summary = useMemo(() => ({
    visits: items.reduce((sum, x) => sum + Number(x.appointments_count || 0), 0),
    clinics: new Set(items.map((x) => x.clinic_id || x.clinic_name).filter(Boolean)).size,
    messages: items.reduce((sum, x) => sum + Number(x.messages_count || 0), 0),
  }), [items]);

  async function load(nextPage = 1, opts = {}) {
    const nextSearch = opts.search ?? search;
    if (opts.soft) setRefreshing(true); else setLoading(true);
    setError("");
    try {
      const [doctors, pending] = await Promise.all([
        patientDoctorsApi.list({ page: nextPage, page_size: PAGE_SIZE, status: "active", search: nextSearch.trim() || undefined }),
        patientDoctorsApi.requests(),
      ]);
      setItems(doctors.items || []);
      setTotal(doctors.total || 0);
      setTotalPages(doctors.total_pages || 0);
      setPage(doctors.page || nextPage);
      setRequests(pending.items || []);
    } catch (err) {
      setError(parseApiError(err, "Failed to load doctors."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function revoke(item) {
    const name = doctorName(item);
    const ok = window.confirm(`${name} will no longer have access to your ongoing care details. Uploaded reports shared with this doctor will be deleted. Remove access?`);
    if (!ok) return;
    try {
      await patientDoctorsApi.revoke(String(item.doctor_id));
      await load(1, { soft: true, search });
    } catch (err) {
      window.alert(parseApiError(err, "Could not remove doctor access."));
    }
  }

  function runSearch(e) {
    e?.preventDefault?.();
    setPage(1);
    load(1, { search });
  }

  function openDoctor(item) {
    navigate(`/patient/doctors/${item.doctor_id}`);
  }

  function stopRowNavigation(event) {
    event.stopPropagation();
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 lg:px-6">
      <div className="mb-5 flex flex-col justify-between gap-4 lg:flex-row lg:items-start">
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-primary">Care team</div>
          <div className="mt-1 text-3xl font-semibold">Doctors</div>
          <div className="mt-1 text-sm text-muted">Manage doctors, messages, visits, shared reports, and bookings from one dashboard.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/patient/doctor-requests" className="rounded-2xl border border-border bg-white px-5 py-3 text-sm font-semibold hover:bg-slate-50">Doctor requests</Link>
          <button type="button" onClick={() => load(page, { soft: true })} className="inline-flex items-center gap-2 rounded-2xl border border-border bg-white px-5 py-3 text-sm font-semibold hover:bg-slate-50">
            <Icon name="refresh" className="h-4 w-4" /> {refreshing || loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-3xl border border-border bg-white p-5 shadow-soft"><div className="text-xs font-semibold uppercase text-muted">Doctors</div><div className="mt-2 text-2xl font-semibold">{loading ? "—" : total}</div></div>
        <div className="rounded-3xl border border-border bg-white p-5 shadow-soft"><div className="text-xs font-semibold uppercase text-muted">Visits</div><div className="mt-2 text-2xl font-semibold">{summary.visits}</div></div>
        <div className="rounded-3xl border border-border bg-white p-5 shadow-soft"><div className="text-xs font-semibold uppercase text-muted">Messages</div><div className="mt-2 text-2xl font-semibold">{summary.messages}</div></div>
        <div className="rounded-3xl border border-border bg-white p-5 shadow-soft"><div className="text-xs font-semibold uppercase text-muted">Requests</div><div className="mt-2 text-2xl font-semibold">{requests.length}</div></div>
      </div>

      {requests.length > 0 ? (
        <Link to="/patient/doctor-requests" className="mt-5 flex items-center gap-4 rounded-3xl border border-amber-200 bg-amber-50 p-5 text-amber-800 shadow-soft">
          <Icon name="bell" className="h-6 w-6 shrink-0" />
          <div className="min-w-0 flex-1"><div className="font-semibold">{requests.length} doctor request{requests.length > 1 ? "s" : ""} waiting</div><div className="mt-1 text-sm text-amber-700">Review before sharing access.</div></div>
          <span className="text-sm font-semibold">Review</span>
        </Link>
      ) : null}

      <form onSubmit={runSearch} className="mt-5 rounded-3xl border border-border bg-white p-5 shadow-soft">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
          <div className="relative md:col-span-9">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted"><Icon name="search" className="h-4 w-4" /></span>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search doctor, specialty or clinic" className="w-full rounded-2xl border border-border bg-white px-4 py-3 pl-11 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <button type="submit" className="btn-primary-gradient rounded-2xl px-5 py-3 text-sm font-semibold text-white md:col-span-2">Search</button>
          <button type="button" onClick={() => { setSearch(""); load(1, { search: "" }); }} className="rounded-2xl border border-border bg-white px-5 py-3 text-sm font-semibold hover:bg-slate-50 md:col-span-1">Reset</button>
        </div>
      </form>

      {error ? <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div> : null}

      <div className="mt-5 overflow-hidden rounded-3xl border border-border bg-white shadow-soft">
        <div className="flex items-center justify-between border-b border-border bg-surface px-5 py-4">
          <div className="text-sm font-semibold">Approved doctors</div>
          <div className="text-sm text-muted">{loading ? "Loading..." : `${total} total`}</div>
        </div>

        <div className="hidden overflow-x-auto lg:block">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-muted">
              <tr>
                <th className="px-5 py-3">Doctor</th>
                <th className="px-5 py-3">Specialty</th>
                <th className="px-5 py-3">Clinic</th>
                <th className="px-5 py-3">History</th>
                <th className="px-5 py-3">Latest message</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                Array.from({ length: 4 }).map((_, index) => <tr key={index}><td colSpan="6" className="px-5 py-5"><div className="h-8 animate-pulse rounded-xl bg-slate-100" /></td></tr>)
              ) : items.length === 0 ? (
                <tr><td colSpan="6" className="px-5 py-8"><EmptyState requests={requests} /></td></tr>
              ) : items.map((item) => {
                const name = doctorName(item);
                const photo = doctorPhoto(item);
                return (
                  <tr
                    key={item.id || item.doctor_id}
                    onClick={() => openDoctor(item)}
                    onKeyDown={(event) => { if (event.key === "Enter") openDoctor(item); }}
                    tabIndex={0}
                    role="button"
                    className="cursor-pointer outline-none transition hover:bg-slate-50 focus:bg-slate-50"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-cyan-500 text-sm font-bold text-white">{photo ? <img src={photo} alt={name} className="h-full w-full object-cover" /> : initials(name)}</span>
                        <span className="min-w-0"><span className="block truncate font-semibold text-slate-900">{name}</span><span className="block truncate text-xs text-muted">{item.doctor_headline || item.doctor_email || "Doctor profile"}</span></span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-muted"><div>{specialtyLabel(item)}</div>{Number(item.experience_years || 0) > 0 ? <div className="mt-1 text-xs">{item.experience_years}+ years experience</div> : null}</td>
                    <td className="px-5 py-4 text-muted">{clinicLabel(item)}</td>
                    <td className="px-5 py-4"><div className="flex flex-wrap gap-2"><Pill tone="info">{Number(item.appointments_count || 0)} visits</Pill><Pill tone="neutral">{Number(item.reports_count || 0)} reports</Pill></div></td>
                    <td className="px-5 py-4"><div className="max-w-[220px]"><div className="truncate text-slate-700">{item.last_message_preview || "Start a secure conversation"}</div><div className="mt-1 text-xs text-muted">{formatMessageTime(item.last_message_at)}</div></div></td>
                    <td className="px-5 py-4" onClick={stopRowNavigation}>
                      <div className="flex justify-end gap-2">
                        <Link to={`/patient/book-appointment?doctor_id=${item.doctor_id}`} className="btn-primary-gradient rounded-xl px-3 py-2 font-semibold text-white">Book</Link>
                        <Link to={`/patient/doctors/${item.doctor_id}/chat`} className="rounded-xl border border-border bg-white px-3 py-2 font-semibold hover:bg-slate-50">Chat</Link>
                        <Link to={`/patient/doctors/${item.doctor_id}/appointments`} className="rounded-xl border border-border bg-white px-3 py-2 font-semibold hover:bg-slate-50">Visits</Link>
                        <button type="button" onClick={() => revoke(item)} className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 font-semibold text-rose-700 hover:bg-rose-100">Remove</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="divide-y divide-border lg:hidden">
          {loading ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="p-5"><div className="h-32 animate-pulse rounded-2xl bg-slate-100" /></div>) : items.length === 0 ? <div className="p-5"><EmptyState requests={requests} /></div> : items.map((item) => {
            const name = doctorName(item);
            const photo = doctorPhoto(item);
            return (
              <div key={item.id || item.doctor_id} onClick={() => openDoctor(item)} className="cursor-pointer p-5 transition hover:bg-slate-50">
                <div className="flex gap-3">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-cyan-500 text-sm font-bold text-white">{photo ? <img src={photo} alt={name} className="h-full w-full object-cover" /> : initials(name)}</div>
                  <div className="min-w-0 flex-1"><div className="font-semibold">{name}</div><div className="mt-1 text-sm text-muted">{specialtyLabel(item)} · {clinicLabel(item)}</div><div className="mt-2 flex flex-wrap gap-2"><Pill tone="info">{Number(item.appointments_count || 0)} visits</Pill><Pill tone="neutral">{Number(item.messages_count || 0)} messages</Pill></div></div>
                </div>
                {item.last_message_preview ? <div className="mt-3 rounded-2xl bg-surface p-3 text-sm text-muted"><span className="font-semibold text-slate-700">Latest:</span> {item.last_message_preview}</div> : null}
                <div className="mt-4 grid grid-cols-2 gap-2" onClick={stopRowNavigation}><Link to={`/patient/book-appointment?doctor_id=${item.doctor_id}`} className="btn-primary-gradient rounded-2xl px-4 py-3 text-center text-sm font-semibold text-white">Book</Link><Link to={`/patient/doctors/${item.doctor_id}/chat`} className="rounded-2xl border border-border bg-white px-4 py-3 text-center text-sm font-semibold">Chat</Link><Link to={`/patient/doctors/${item.doctor_id}/reports`} className="rounded-2xl border border-border bg-white px-4 py-3 text-center text-sm font-semibold">Reports</Link><button onClick={() => revoke(item)} className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">Remove</button></div>
              </div>
            );
          })}
        </div>

        {totalPages > 1 ? (
          <div className="flex items-center justify-between border-t border-border px-5 py-4 text-sm">
            <button type="button" disabled={page <= 1} onClick={() => load(page - 1, { soft: true })} className="rounded-2xl border border-border bg-white px-4 py-2 font-semibold disabled:opacity-50">Previous</button>
            <span className="text-muted">Page {page} of {totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => load(page + 1, { soft: true })} className="rounded-2xl border border-border bg-white px-4 py-2 font-semibold disabled:opacity-50">Next</button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
